/**
 * Scrubs bearer credentials out of URLs before they reach an error tracker.
 *
 * Two kinds of URL carry one:
 * - Family links, `/family/{token}` and `/api/family/{token}/claim|approve`.
 *   The token is a 43-character base64url value that opens a draft memorial,
 *   so any 20+ character segment after `/family/` is treated as one.
 * - Unsubscribe links, `?token=…`.
 *
 * Shared by the browser and server Sentry wrappers; no SDK imports here.
 */

const FAMILY_TOKEN_SEGMENT = /\/family\/[A-Za-z0-9_-]{20,}/g;
const TOKEN_QUERY_PARAM = /(^|[?&;])(token)=[^&#\s]*/gi;

/** Replace family tokens with `[token]` and the value of any `token=` parameter with `[redacted]`. */
export function scrubSensitiveUrl(value: string): string {
  return value
    .replace(FAMILY_TOKEN_SEGMENT, "/family/[token]")
    .replace(TOKEN_QUERY_PARAM, "$1$2=[redacted]");
}

type BreadcrumbLike = {
  message?: string;
  data?: Record<string, unknown>;
};

const BREADCRUMB_URL_KEYS = ["url", "from", "to"] as const;

/**
 * Apply `scrubSensitiveUrl` to a breadcrumb's `message` and to `data.url`,
 * `data.from` and `data.to` (fetch/xhr and navigation breadcrumbs). Returns a
 * copy; the argument is not modified.
 */
export function scrubBreadcrumbUrls<T extends BreadcrumbLike>(breadcrumb: T): T {
  const scrubbed: T = { ...breadcrumb };

  if (typeof scrubbed.message === "string") {
    scrubbed.message = scrubSensitiveUrl(scrubbed.message);
  }

  if (scrubbed.data && typeof scrubbed.data === "object") {
    const data: Record<string, unknown> = { ...scrubbed.data };
    for (const key of BREADCRUMB_URL_KEYS) {
      const value = data[key];
      if (typeof value === "string") data[key] = scrubSensitiveUrl(value);
    }
    scrubbed.data = data;
  }

  return scrubbed;
}
